<template>
  <div
    :class="['bg-gray-200 dark:bg-gray-700 relative inline-block font-semibold text-blue dark:text-white rounded-full', {
      'text-xs': size === 'sm',
      'text-lg': size === 'md',
      'text-2xl': size === 'lg',
    }]"
    :style="{ width, height }"
  >
    <div
      :class="['absolute inset-0 rounded-full overflow-hidden', color, rotation]"
      :style="{ clip: `rect(0 ${half} ${full} 0)` }"
    />
    <div
      class="absolute inset-0 rounded-full overflow-hidden"
      :class="score > 5 ? color : colors[0]"
      :style="{ clip: score > 5 ? `rect(0 ${full} ${full} ${half})` : `rect(0 ${half} ${full} 0)` }"
    />
    <div class="absolute inset-0" :style="{ padding }">
      <div
        class="bg-white dark:bg-gray-900 flex items-center justify-center size-full rounded-full"
        :aria-label="`Job Score: ${score || '—'}`"
      >
        {{ score || '—' }}
      </div>
    </div>
  </div>
</template>

<script setup>
// Source: https://codepen.io/toddscottik/pen/gOpqapg

import { computed } from "vue"

const props = defineProps({
  school: Object,
  size: String,
  value: Number,
})

const colors = {
  0: "bg-inherit",
  10: "bg-red-500",
  20: "bg-red-400",
  30: "bg-orange-400",
  40: "bg-amber-500",
  50: "bg-yellow-500",
  60: "bg-lime-400",
  70: "bg-green-400",
  80: "bg-green-500",
  90: "bg-green-600",
  100: "bg-green-700",
}

const sizes = {
  sm: 25,
  md: 50,
  lg: 75,
}

const paddings = {
  sm: 1.875,
  md: 5,
  lg: 7,
}

const rotations = {
  0: "hidden",
  5: "rotate-[18deg]",
  10: "rotate-[36deg]",
  20: "rotate-[72deg]",
  30: "rotate-[108deg]",
  40: "rotate-[144deg]",
  50: "rotate-180",
  60: "rotate-[216deg]",
  70: "rotate-[252deg]",
  80: "rotate-[288deg]",
  90: "rotate-[324deg]",
  100: "rotate-[360deg]",
}

const color = computed(() => colors[rounded.value] ?? colors[10])

const score = computed(() => {
  if (props.value) {
    return parseFloat(props.value).toFixed(1)
  } else if (props.school?.job_score) {
    return parseFloat(props.school.job_score).toFixed(1)
  }

  return null
})

const number = computed(() => sizes[props.size])

const width = computed(() => `${number.value}px`)

const height = computed(() => `${number.value}px`)

const full = computed(() => `${number.value}px`)

const half = computed(() => `${number.value / 2}px`)

const padding = computed(() => `${paddings[props.size]}px`)

const rotation = computed(() => rotations[rounded.value])

const rounded = computed(() => (score.value ? Math.max(10, parseInt(score.value) * 10) : 0))
</script>
